import React, { useState, useEffect } from 'react';
import { ViewState, Order, OrderStatus, User } from './types';
import Login from './components/Login.tsx';
import Layout from './components/Layout.tsx';
import Dashboard from './components/Dashboard.tsx';
import OrderForm from './components/OrderForm.tsx';
import Admin from './components/Admin.tsx';
import Customers from './components/Customers.tsx';
import PublicWebsite from './components/PublicWebsite.tsx';
import * as storage from './services/storageService.ts';
import * as authService from './services/authService.ts';
import { getDatabaseDiagnostics } from './services/dbService.ts';
import { generateEasternTimestamp } from './utils';
import { registerServiceWorker } from './services/serviceWorkerService.ts';

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [view, setView] = useState<ViewState>('PUBLIC_SITE');
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);

  // Data State
  const [orders, setOrders] = useState<Order[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoaded, setCustomersLoaded] = useState<boolean>(false);
  const [loadingCustomers, setLoadingCustomers] = useState<boolean>(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  // State to track database connection status
  const [dbConnected, setDbConnected] = useState<boolean>(false);
  const [dbError, setDbError] = useState<string | null>(null);
  const [dbDiagnostics, setDbDiagnostics] = useState<any>(null);

  useEffect(() => {
    // Register service worker for cache management and updates
    registerServiceWorker();

    const init = async () => {
      try {
        // Initialize storage and check if successful
        const storageInitialized = await storage.initStorage();

        if (!storageInitialized) {
          console.error('Failed to initialize storage. Database connection may not be available.');
          setDbError('Failed to initialize storage. Please check your database configuration and try again.');
          setDbConnected(false);
          return;
        }

        setDbConnected(true);
        setDbError(null);

        // Check if user is already logged in
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          setView('DASHBOARD');
          // Only load data after user is authenticated
          await refreshData();
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        console.error('Error during initialization:', errorMsg);
        setDbError(`An error occurred during initialization: ${errorMsg}`);
        setDbConnected(false);
      }
    };

    init();
  }, []);

  const refreshData = async () => {
    try {
      // Check database connection before fetching data
      if (!dbConnected) {
        console.log('Database connection lost, attempting to reconnect...');

        // Try to reinitialize storage
        const storageInitialized = await storage.initStorage();
        if (!storageInitialized) {
          setDbError('Database connection lost. Please try again.');
          setDbConnected(false);
          return;
        }

        console.log('Database connection restored successfully');
        setDbConnected(true);
        setDbError(null);
      }

      const [ordersData, productsData, categoriesData] = await Promise.all([
        storage.getOrderSummaries(), // Use summaries instead of full order details
        storage.getProducts(),
        storage.getCategories()
      ]);

      setOrders(ordersData);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('Error refreshing data:', errorMsg);

      // Check if this is a database connection error
      if (error instanceof Error && error.message.includes('Database connection pool is not available')) {
        setDbError('Database connection lost. Please reload the application.');
        setDbConnected(false);
      }

      // Ensure we at least have empty arrays if there's an error
      setOrders([]);
      setProducts([]);
      setCategories([]);
    }
  };

  const loadCustomersData = async () => {
    if (customersLoaded || loadingCustomers) return;

    setLoadingCustomers(true);
    try {
      // Check database connection before fetching data
      if (!dbConnected) {
        console.log('Database connection lost, attempting to reconnect...');

        // Try to reinitialize storage
        const storageInitialized = await storage.initStorage();
        if (!storageInitialized) {
          setDbError('Database connection lost. Please try again.');
          setDbConnected(false);
          return;
        }

        console.log('Database connection restored successfully');
        setDbConnected(true);
        setDbError(null);
      }

      const customersData = await storage.getCustomers();
      setCustomers(customersData);
      setCustomersLoaded(true);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('Error loading customers data:', errorMsg);
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleLogin = async (username: string) => {
    // The username parameter is actually the user's name from Google Auth
    // The full user object is already stored in localStorage by authService
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      setView('DASHBOARD');

      // Load data after login
      await refreshData();
    }
  };

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    setView('LOGIN');
  };

  const handleSaveOrder = async (order: Order) => {
    await storage.saveOrder(order);
    await refreshData();
    setView('DASHBOARD');
    setEditingOrder(null);
  };

  const handleStatusUpdate = async (id: string, status: OrderStatus) => {
    const order = orders.find(o => o.id === id);
    if (order) {
      // Update just the order status in the database
      await storage.updateOrderStatus(id, status, user ? user.name : 'System');
      await refreshData();
    }
  };

  const handleItemToggle = async (orderId: string, itemId: string) => {
    const orderIndex = orders.findIndex(o => o.id === orderId);
    if (orderIndex === -1) return;

    // Find the specific item to toggle
    const order = orders[orderIndex];
    const item = order.items.find(i => i.id === itemId);
    if (!item) return;

    // Toggle the item's status
    const newIsDone = !item.isDone;

    // Update just the item status in the database
    await storage.updateOrderItemStatus(orderId, itemId, newIsDone);

    // Check if all items would be done after this update
    const allItemsDone = order.items.length > 0 && 
      order.items.every(i => i.id === itemId ? newIsDone : i.isDone);

    // Auto-update status logic
    // Only transition if currently Pending, Website Submitted, or Done.
    // We avoid changing Picked Up or Cancelled automatically as those imply specific lifecycle ends.
    if (['Pending', 'Website Submitted', 'Done'].includes(order.status)) {
        if (allItemsDone && order.status !== 'Done') {
            // Update order status to Done
            await storage.updateOrderStatus(orderId, 'Done', user ? user.name : 'System');
        } else if (!allItemsDone && order.status === 'Done') {
            // Update order status to Pending
            await storage.updateOrderStatus(orderId, 'Pending', user ? user.name : 'System');
        }
    }

    await refreshData();
  };

  const startEditOrder = async (order: Order) => {
    setEditingOrder(order);
    // Load customers data before showing the edit order form
    await loadCustomersData();
    setView('NEW_ORDER');
  };

  const startNewOrder = async () => {
      setEditingOrder(null);
      // Load customers data before showing the new order form
      await loadCustomersData();
      setView('NEW_ORDER');
  };

  // Database Error Screen
  if (dbError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-lg w-full">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Database Connection Error</h2>
          <p className="text-gray-700 mb-4">{dbError}</p>

          <div className="mb-6 text-sm">
            <h3 className="font-semibold text-gray-800 mb-2">Troubleshooting Steps:</h3>
            <ol className="list-decimal pl-5 mb-4 space-y-1">
              <li>Check that your database server is running</li>
              <li>Ensure your database user has the necessary permissions</li>
              <li>Check for network or firewall issues that might block the connection</li>
              <li>Verify that the database exists and is accessible</li>
              <li>Ensure SSL is properly configured for the database connection</li>
            </ol>
          </div>

          <div className="flex space-x-4">
            <button 
              onClick={() => window.location.reload()} 
              className="flex-1 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition-colors"
            >
              Retry Connection
            </button>
            <button 
              onClick={() => setView('PUBLIC_SITE')} 
              className="flex-1 bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors"
            >
              Continue to Website
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Route 1: Public Website (Default, accessible by all)
  if (view === 'PUBLIC_SITE') {
    return <PublicWebsite onLoginClick={() => setView('LOGIN')} />;
  }

  // Route 2: Login Screen (If not authenticated)
  if (!user) {
    return <Login onLogin={handleLogin} onBack={() => setView('PUBLIC_SITE')} />;
  }

  // Route 3: Authenticated Order Management System
  return (
    <Layout 
        currentView={view} 
        setView={(v) => {
            if(v === 'NEW_ORDER') {
                setEditingOrder(null);
                // Load customers data when switching to NEW_ORDER view
                loadCustomersData();
            }
            if(v === 'CUSTOMERS') loadCustomersData();
            setView(v);
        }} 
        user={user} 
        onLogout={handleLogout}
    >
      {view === 'DASHBOARD' && (
        <Dashboard 
          orders={orders} 
          onUpdateStatus={handleStatusUpdate}
          onEditOrder={startEditOrder}
          onItemToggle={handleItemToggle}
        />
      )}

      {view === 'NEW_ORDER' && (
        <OrderForm 
          existingOrder={editingOrder}
          onSave={handleSaveOrder}
          onCancel={() => setView('DASHBOARD')}
          customers={customers}
          products={products}
          categories={categories}
          user={user}
          loading={loadingCustomers}
        />
      )}

      {view === 'CUSTOMERS' && (
        <Customers 
            customers={customers} 
            orders={orders}
            onViewOrder={startEditOrder}
            loading={loadingCustomers}
        />
      )}

      {view === 'ADMIN' && (
        <Admin 
            products={products}
            categories={categories}
            customers={customers}
            refreshData={refreshData}
        />
      )}
    </Layout>
  );
};

export default App;
